import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, Coins } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const linkBase =
  "relative text-sm tracking-tight font-medium text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors duration-200";
const linkActive = "text-[#0A0A0A]";

const NavItems = ({ onNavigate }) => {
  const { t } = useApp();
  const items = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/about", label: t.nav.about, testid: "nav-about" },
    { to: "/portfolio", label: t.nav.portfolio, testid: "nav-portfolio" },
    { to: "/pricing", label: t.nav.pricing, testid: "nav-pricing" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
  ];
  return (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          data-testid={item.testid}
          onClick={onNavigate}
          className={({ isActive }) => cn(linkBase, isActive && linkActive)}
        >
          {({ isActive }) => (
            <span className="flex items-center gap-2">
              {item.label}
              <span
                className={cn(
                  "block h-[6px] w-[6px] rounded-none bg-[#3A00FF] transition-opacity",
                  isActive ? "opacity-100" : "opacity-0",
                )}
              />
            </span>
          )}
        </NavLink>
      ))}
    </>
  );
};

const LangToggle = ({ compact = false }) => {
  const { lang, setLang } = useApp();
  return (
    <div
      className={cn(
        "inline-flex items-center border border-[#0A0A0A] text-xs font-bold tracking-widest uppercase",
        compact ? "" : "shadow-[3px_3px_0px_#0A0A0A]",
      )}
      role="group"
      aria-label="Language"
    >
      <Globe className="h-3.5 w-3.5 mx-2 text-[#0A0A0A]/70" aria-hidden />
      <button
        type="button"
        data-testid="lang-toggle-en"
        onClick={() => setLang("en")}
        className={cn(
          "px-3 py-1.5 transition-colors",
          lang === "en"
            ? "bg-[#0A0A0A] text-white"
            : "bg-transparent text-[#0A0A0A] hover:bg-[#0A0A0A]/5",
        )}
        aria-pressed={lang === "en"}
      >
        EN
      </button>
      <span className="w-px self-stretch bg-[#0A0A0A]" />
      <button
        type="button"
        data-testid="lang-toggle-de"
        onClick={() => setLang("de")}
        className={cn(
          "px-3 py-1.5 transition-colors",
          lang === "de"
            ? "bg-[#0A0A0A] text-white"
            : "bg-transparent text-[#0A0A0A] hover:bg-[#0A0A0A]/5",
        )}
        aria-pressed={lang === "de"}
      >
        DE
      </button>
    </div>
  );
};

const CurrencyToggle = ({ compact = false }) => {
  const { currency, setCurrency } = useApp();
  return (
    <div
      className={cn(
        "inline-flex items-center border border-[#0A0A0A] text-xs font-bold tracking-widest uppercase",
        compact ? "" : "shadow-[3px_3px_0px_#3A00FF]",
      )}
      role="group"
      aria-label="Currency"
    >
      <Coins className="h-3.5 w-3.5 mx-2 text-[#0A0A0A]/70" aria-hidden />
      <button
        type="button"
        data-testid="currency-toggle-eur"
        onClick={() => setCurrency("EUR")}
        className={cn(
          "px-3 py-1.5 transition-colors",
          currency === "EUR"
            ? "bg-[#3A00FF] text-white"
            : "bg-transparent text-[#0A0A0A] hover:bg-[#0A0A0A]/5",
        )}
        aria-pressed={currency === "EUR"}
      >
        €
      </button>
      <span className="w-px self-stretch bg-[#0A0A0A]" />
      <button
        type="button"
        data-testid="currency-toggle-usd"
        onClick={() => setCurrency("USD")}
        className={cn(
          "px-3 py-1.5 transition-colors",
          currency === "USD"
            ? "bg-[#3A00FF] text-white"
            : "bg-transparent text-[#0A0A0A] hover:bg-[#0A0A0A]/5",
        )}
        aria-pressed={currency === "USD"}
      >
        $
      </button>
    </div>
  );
};

export { LangToggle, CurrencyToggle };

const Navbar = () => {
  const { t } = useApp();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-white/75 backdrop-blur-xl border-b border-[#0A0A0A]/10"
          : "bg-transparent border-b border-transparent",
      )}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-6">
          <Link
            to="/"
            data-testid="brand-logo"
            className="group inline-flex items-center"
          >
            <img
              src="/brand/netovibe-logo.png"
              alt="NetoVibe — web design studio"
              className="h-9 lg:h-11 w-auto object-contain group-hover:opacity-80 transition-opacity"
              width="140"
              height="44"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <NavItems />
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LangToggle />
            <CurrencyToggle />
            <Link to="/contact">
              <Button
                data-testid="nav-cta"
                className="h-10 rounded-none bg-[#0A0A0A] text-white hover:bg-[#3A00FF] transition-colors px-5 text-xs tracking-widest uppercase font-bold"
              >
                {t.nav.cta}
              </Button>
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="mobile-menu-trigger"
                  aria-label={t.common.openMenu}
                  className="inline-flex items-center justify-center h-10 w-10 border border-[#0A0A0A]"
                >
                  {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                data-testid="mobile-menu-content"
                className="w-full sm:max-w-sm bg-white border-l border-[#0A0A0A]"
              >
                <SheetTitle className="sr-only">NetoVibe navigation menu</SheetTitle>
                <SheetDescription className="sr-only">
                  Site navigation, language and currency toggles.
                </SheetDescription>
                <div className="flex flex-col gap-8 pt-8">
                  <img
                    src="/brand/netovibe-logo.png"
                    alt="NetoVibe logo"
                    className="h-10 w-auto object-contain"
                    width="140"
                    height="40"
                  />
                  <nav className="flex flex-col gap-5 text-lg">
                    <NavItems onNavigate={() => setOpen(false)} />
                  </nav>
                  <div className="flex flex-col gap-3">
                    <LangToggle compact />
                    <CurrencyToggle compact />
                  </div>
                  <Link to="/contact" onClick={() => setOpen(false)}>
                    <Button
                      data-testid="mobile-nav-cta"
                      className="w-full h-11 rounded-none bg-[#0A0A0A] text-white hover:bg-[#3A00FF]"
                    >
                      {t.nav.cta}
                    </Button>
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
